/**
 * Alishba Fashions - Homepage shop shell.
 * Keeps category browsing on the homepage while reusing the
 * standalone collection section as the server-rendered source of truth.
 */
(function () {
  'use strict';

  function initHomeShopShell() {
    var shell = document.querySelector('[data-home-shop-shell]');
    if (!shell) return;

    var content = shell.querySelector('[data-home-shop-content]');
    var status = shell.querySelector('[data-home-shop-status]');
    var sectionId = shell.getAttribute('data-collection-section-id') || 'main-collection';
    var sectionType = shell.getAttribute('data-collection-section-type') || 'alishba-main-collection';
    var activeRequestId = 0;
    var currentShopTarget = null;
    var defaultShopTarget = normalizeShopTarget(shell.getAttribute('data-default-shop-target')) || '/collections/all';

    function normalizeShopTarget(rawTarget) {
      if (!rawTarget) return null;

      try {
        var url = new URL(rawTarget, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        if (url.pathname.indexOf('/collections/') !== 0) return null;
        return url.pathname + url.search;
      } catch (error) {
        return null;
      }
    }

    function buildHomeUrl(target) {
      var url = new URL(window.location.href);
      url.search = '';
      url.hash = 'shop';

      if (target && target !== defaultShopTarget) {
        url.searchParams.set('shop_target', target);
      }

      return url.pathname + url.search + url.hash;
    }

    function getTargetFromLocation() {
      var params = new URLSearchParams(window.location.search);
      return normalizeShopTarget(params.get('shop_target')) || defaultShopTarget;
    }

    function setLoadingState(isLoading, message) {
      shell.classList.toggle('is-loading', isLoading);
      shell.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      if (!status) return;

      status.hidden = !isLoading;
      status.textContent = message || 'Loading products...';
    }

    function refreshCollectionUi() {
      if (window.AlishbaCollection) {
        if (typeof window.AlishbaCollection.init === 'function') {
          window.AlishbaCollection.init(content, { allowNavigation: false });
        }
        if (typeof window.AlishbaCollection.updateLayoutMetrics === 'function') {
          window.AlishbaCollection.updateLayoutMetrics(shell);
        }
        if (typeof window.AlishbaCollection.refreshPriceLabels === 'function') {
          window.AlishbaCollection.refreshPriceLabels(content);
        }
      }

      if (window.AlishbaStorefront && typeof window.AlishbaStorefront.initSwatchImageHover === 'function') {
        window.AlishbaStorefront.initSwatchImageHover(content);
      }
    }

    function getStickyOffset() {
      var headerGroup = document.querySelector('.shopify-section-group-header-group');
      var announcementBar = document.querySelector('.announcement');
      var siteHeader = document.querySelector('.site-header');
      var headerMain = document.querySelector('.header-main');
      var categoryStrip = document.querySelector('.category-strip');
      var navOffset = (announcementBar ? announcementBar.getBoundingClientRect().height : 0)
        + (headerMain ? headerMain.getBoundingClientRect().height : 0)
        + (categoryStrip ? categoryStrip.getBoundingClientRect().height : 0);
      var fallbackOffset = headerGroup ? headerGroup.getBoundingClientRect().height : (siteHeader ? siteHeader.getBoundingClientRect().height : 0);

      return Math.round(navOffset || fallbackOffset || 0);
    }

    function getScrollExtraOffset() {
      if (window.innerWidth <= 480) return 20;
      if (window.innerWidth <= 600) return 24;
      if (window.innerWidth <= 768) return 28;
      if (window.innerWidth <= 1024) return 32;
      return 40;
    }

    function scrollShellIntoView(behavior) {
      var sectionHeading = content.querySelector('#section-heading') || document.getElementById('section-heading');
      var scrollTarget = sectionHeading || shell;

      if (!scrollTarget) return;

      var targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - getStickyOffset() - getScrollExtraOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: behavior || 'smooth'
      });
    }

    function closeOverlayUI() {
      var navDrawer = document.querySelector('[data-nav-drawer]');
      var navToggle = document.querySelector('[data-nav-toggle]');
      var navBackdrop = document.querySelector('[data-nav-backdrop]');
      var filterShell = content.querySelector('#mobile-filter-shell');
      var filterToggle = content.querySelector('#mobile-filter-toggle');
      var focused = document.activeElement;

      document.body.classList.remove('nav-open');
      document.body.classList.remove('filter-drawer-open');

      if (navDrawer) navDrawer.setAttribute('hidden', '');
      if (navBackdrop) navBackdrop.setAttribute('hidden', '');
      if (navToggle) navToggle.setAttribute('aria-expanded', 'false');
      if (filterShell) filterShell.setAttribute('hidden', '');
      if (filterToggle) filterToggle.setAttribute('aria-expanded', 'false');
      if (focused && focused.closest('.category-strip')) focused.blur();
    }

    function setActiveNavigation(target) {
      var normalizedTarget = normalizeShopTarget(target) || defaultShopTarget;

      document.querySelectorAll('.category-strip .cat-link').forEach(function (link) {
        link.classList.remove('active');
        link.removeAttribute('aria-current');
      });

      var navigationLinks = document.querySelectorAll('[data-home-shop-link][data-shop-target]');

      navigationLinks.forEach(function (link) {
        link.classList.remove('active');
        link.removeAttribute('aria-current');
      });

      document.querySelectorAll('.nav-drawer-group-toggle.active').forEach(function (toggle) {
        toggle.classList.remove('active');
      });

      navigationLinks.forEach(function (link) {
        var linkTarget = normalizeShopTarget(link.getAttribute('data-shop-target'));
        if (linkTarget !== normalizedTarget) return;

        link.classList.add('active');
        link.setAttribute('aria-current', 'page');

        var catItem = link.closest('.cat-item');
        if (catItem) {
          var parentLink = catItem.querySelector('.cat-link');
          if (parentLink) parentLink.classList.add('active');
        }

        var drawerGroup = link.closest('.nav-drawer-group');
        if (drawerGroup) {
          var drawerToggle = drawerGroup.querySelector('.nav-drawer-group-toggle');
          if (drawerToggle) drawerToggle.classList.add('active');
        }
      });

      var activeCategoryLink = document.querySelector('.category-strip .cat-link.active');
      if (activeCategoryLink && typeof activeCategoryLink.scrollIntoView === 'function') {
        activeCategoryLink.scrollIntoView({
          inline: 'nearest',
          block: 'nearest',
          behavior: 'auto'
        });
      }
    }

    function buildSectionRequestUrl(target, paramName, paramValue) {
      var url = new URL(target, window.location.origin);
      url.searchParams.set(paramName, paramValue);
      return url.toString();
    }

    function getSectionRequestCandidates(target) {
      var candidates = [];

      candidates.push({
        kind: 'html',
        url: buildSectionRequestUrl(target, 'section_id', sectionId)
      });

      if (sectionType && sectionType !== sectionId) {
        candidates.push({
          kind: 'html',
          url: buildSectionRequestUrl(target, 'section_id', sectionType)
        });
      }

      candidates.push({
        kind: 'json',
        url: buildSectionRequestUrl(target, 'sections', sectionId)
      });

      return candidates;
    }

    function parseSectionResponse(candidate, response) {
      if (candidate.kind === 'json') {
        return response.json().then(function (payload) {
          if (!payload || typeof payload !== 'object') {
            throw new Error('Invalid section payload');
          }

          return payload[sectionId] || payload[sectionType] || '';
        });
      }

      return response.text();
    }

    function fetchSectionMarkup(target) {
      var candidates = getSectionRequestCandidates(target);
      var index = 0;

      function isCollectionMarkup(markup) {
        return typeof markup === 'string'
          && markup.trim() !== ''
          && (markup.indexOf('collection-shell') !== -1
            || markup.indexOf('data-collection-filter-form') !== -1
            || markup.indexOf('shop-section-head') !== -1);
      }

      function tryNext(previousError) {
        if (index >= candidates.length) {
          return Promise.reject(previousError || new Error('Unable to load shop section'));
        }

        var candidate = candidates[index++];

        return fetch(candidate.url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(function (response) {
            if (!response.ok) {
              throw new Error('Failed to fetch shop section');
            }

            return parseSectionResponse(candidate, response);
          })
          .then(function (markup) {
            if (!isCollectionMarkup(markup)) {
              throw new Error('Missing collection section markup');
            }

            return markup;
          })
          .catch(function (error) {
            return tryNext(error);
          });
      }

      return tryNext();
    }

    function buildTemplateFromMarkup(markup) {
      var parser = new DOMParser();
      var parsed = parser.parseFromString(markup, 'text/html');
      var collectionRoot = parsed.querySelector('.collection-shell');
      var template = document.createElement('template');

      template.innerHTML = collectionRoot ? collectionRoot.outerHTML : markup.trim();
      template.content.querySelectorAll('script').forEach(function (script) {
        script.remove();
      });

      var nestedShopRoot = template.content.querySelector('#shop');
      if (nestedShopRoot) {
        nestedShopRoot.removeAttribute('id');
      }

      return template;
    }

    function submitCollectionForm(form) {
      if (!form) return;

      var targetUrl = new URL(currentShopTarget || defaultShopTarget, window.location.origin);
      var formData = new FormData(form);
      var params = new URLSearchParams();

      formData.forEach(function (value, key) {
        if (value === '' || value == null) return;
        params.append(key, value);
      });

      params.delete('page');
      targetUrl.search = params.toString();

      loadShopTarget(targetUrl.pathname + targetUrl.search, {
        updateHistory: true
      });
    }

    function updateSortTarget(value) {
      var targetUrl = new URL(currentShopTarget || defaultShopTarget, window.location.origin);
      targetUrl.searchParams.set('sort_by', value);
      targetUrl.searchParams.delete('page');

      loadShopTarget(targetUrl.pathname + targetUrl.search, {
        updateHistory: true
      });
    }

    function loadShopTarget(target, options) {
      var normalizedTarget = normalizeShopTarget(target) || defaultShopTarget;
      var requestId = ++activeRequestId;
      var shouldNavigateOnError = options && options.navigateOnError;

      currentShopTarget = normalizedTarget;
      closeOverlayUI();
      if (options && options.scrollTo) {
        scrollShellIntoView(options.scrollBehavior || 'smooth');
      }
      setLoadingState(true, 'Loading products...');

      return fetchSectionMarkup(normalizedTarget)
        .then(function (markup) {
          if (requestId !== activeRequestId) return;

          var template = buildTemplateFromMarkup(markup);
          content.innerHTML = '';
          content.appendChild(template.content);
          currentShopTarget = normalizedTarget;
          refreshCollectionUi();
          setActiveNavigation(normalizedTarget);
          if (window.AlishbaStorefront && typeof window.AlishbaStorefront.initDelayedProductImages === 'function') {
            window.AlishbaStorefront.initDelayedProductImages(content, { delay: false });
          }

          if (options && options.updateHistory) {
            var nextUrl = buildHomeUrl(normalizedTarget);
            window.history.pushState({ shopTarget: normalizedTarget }, '', nextUrl);
          }

          if (options && options.scrollTo) {
            scrollShellIntoView(options.scrollBehavior || 'smooth');
          }
        })
        .catch(function () {
          if (requestId !== activeRequestId) return;

          if (shouldNavigateOnError) {
            window.location.assign(normalizedTarget);
            return;
          }

          setLoadingState(false, 'Unable to load products right now.');
        })
        .finally(function () {
          if (requestId === activeRequestId) {
            setLoadingState(false);
          }
        });
    }

    function loadMoreProducts(btn) {
      var page       = parseInt(btn.getAttribute('data-page'), 10) || 1;
      var totalPages = parseInt(btn.getAttribute('data-total-pages'), 10) || 1;
      var nextUrl    = btn.getAttribute('data-next-url');
      var sectionId  = btn.getAttribute('data-section-id') || 'alishba-main-collection';

      if (page >= totalPages || !nextUrl) return;

      btn.setAttribute('data-loading', '');

      var fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var parser   = new DOMParser();
          var doc      = parser.parseFromString(html, 'text/html');
          var newCards = doc.querySelectorAll('#product-grid .product-card');
          var grid     = document.getElementById('product-grid');

          if (grid && newCards.length) {
            newCards.forEach(function (card) { grid.appendChild(card); });

            if (window.AlishbaStorefront && typeof window.AlishbaStorefront.initSwatchImageHover === 'function') {
              window.AlishbaStorefront.initSwatchImageHover(grid);
            }
            if (window.AlishbaStorefront && typeof window.AlishbaStorefront.initDelayedProductImages === 'function') {
              window.AlishbaStorefront.initDelayedProductImages(grid, { delay: false });
            }
          }

          var nextPage    = page + 1;
          var newNextUrl  = doc.querySelector('[data-load-more]');
          var nextDataUrl = newNextUrl ? newNextUrl.getAttribute('data-next-url') : null;

          btn.setAttribute('data-page', nextPage);

          if (nextPage >= totalPages || !nextDataUrl) {
            btn.style.display = 'none';
            var endMsg = btn.nextElementSibling;
            if (endMsg && endMsg.classList.contains('load-more-end')) {
              endMsg.style.display = 'block';
            }
          } else {
            btn.setAttribute('data-next-url', nextDataUrl);
          }
        })
        .catch(function () {})
        .finally(function () {
          btn.removeAttribute('data-loading');
        });
    }

    document.addEventListener('click', function (event) {
      var loadMoreBtn = event.target.closest('[data-load-more]');
      if (loadMoreBtn) {
        event.preventDefault();
        loadMoreProducts(loadMoreBtn);
        return;
      }

      var navLink = event.target.closest('[data-home-shop-link][data-shop-target]');
      if (navLink) {
        var navTarget = normalizeShopTarget(navLink.getAttribute('data-shop-target'));
        if (!navTarget) return;

        event.preventDefault();
        loadShopTarget(navTarget, {
          updateHistory: true,
          scrollTo: true,
          navigateOnError: true
        });
        return;
      }

      var shellLink = event.target.closest('a[href]');
      if (!shellLink || !content.contains(shellLink)) return;
      if (shellLink.closest('.product-grid')) return;

      var linkTarget = normalizeShopTarget(shellLink.getAttribute('href'));
      if (!linkTarget) return;
      var shouldScrollToShop = !!shellLink.closest('.pagination-block, .shop-section-head, [data-collection-filter-form]');

      event.preventDefault();
      loadShopTarget(linkTarget, {
        updateHistory: true,
        scrollTo: shouldScrollToShop,
        navigateOnError: true
      });
    });

    shell.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-collection-filter-form]');
      if (!form) return;

      event.preventDefault();
      submitCollectionForm(form);
    });

    shell.addEventListener('input', function (event) {
      if (event.target.matches('#price-min-desktop, #price-max-desktop, #price-min-mobile, #price-max-mobile')) {
        if (window.AlishbaCollection && typeof window.AlishbaCollection.refreshPriceLabels === 'function') {
          window.AlishbaCollection.refreshPriceLabels(content);
        }
      }
    });

    shell.addEventListener('change', function (event) {
      if (event.target.matches('[data-filter-checkbox]')) {
        submitCollectionForm(event.target.closest('[data-collection-filter-form]'));
        return;
      }

      if (event.target.matches('#price-min-desktop, #price-max-desktop, #price-min-mobile, #price-max-mobile')) {
        submitCollectionForm(event.target.closest('[data-collection-filter-form]'));
        return;
      }

      if (event.target.matches('#SortBy, [data-sort-select]')) {
        updateSortTarget(event.target.value);
      }
    });

    window.addEventListener('popstate', function () {
      loadShopTarget(getTargetFromLocation(), {
        updateHistory: false,
        scrollTo: window.location.hash === '#shop'
      });
    });

    setActiveNavigation(getTargetFromLocation());
    refreshCollectionUi();
    loadShopTarget(getTargetFromLocation(), {
      updateHistory: false,
      scrollTo: false,
      navigateOnError: window.location.search.indexOf('shop_target=') !== -1
    });

    window.addEventListener('load', function () {
      if (content.querySelector('.collection-shell')) return;

      loadShopTarget(getTargetFromLocation(), {
        updateHistory: false,
        scrollTo: false,
        navigateOnError: window.location.search.indexOf('shop_target=') !== -1
      });
    });
  }

  document.addEventListener('DOMContentLoaded', initHomeShopShell);
})();
